import React, { useCallback, useEffect, useMemo, useState } from "react";
import httpClient from "../../../../../api/httpClient";
import { fetchCalendarByRef } from "../../../Appointments/TabContent/api/AppointmentsApi";
import { parseCalendarApiResponseToDockSettings } from "../../../Appointments/TabContent/utils/dockSettingsParser";
import { Building2, Pencil } from "lucide-react";
import DockDoorSettings from "../../../Appointments/TabContent/Calendar-Main/DockDoorSettings";
import EditWarehouseModal from "./EditWarehouseModal";
import Loader from "../../../../../components/ACS-Dashboard/ACS-Blocks/Loader";
import { linkifyText, linkifyEmails } from "../../../../../components/ACS-Dashboard/ACS-Utils/textFormatting";
import { getAllTimezones, getUtcOffsetLabel } from "../../workingHoursUtils";
import { usePrivilege } from "../../../../../guard/usePrivilege";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { addChildLocation, searchCountries, searchStatesByCountry, searchCitiesByCountryState, saveNewCity } from "../../api";
import GeoTypeaheadField from "../../../../../components/ACS-Dashboard/ACS-Blocks/GeoTypeaheadField";
import { DisplayDetail } from "../../../../../components/ACS-Dashboard/ACS-Blocks/DisplayDetail";
import {
  sectionCardClass,
  sectionHeaderBarClass,
  sectionFieldGridClass,
  sectionContentClass,
  FormField,
  ErrorHint,
} from "../../../../../components/ACS-Dashboard/ACS-Blocks/sectionStyles";


const BLANK_NEW_LOCATION = {
  locationName: "",
  locationType: "WAREHOUSE",
  customLocationIdentifier: "",
  addressLine1: "",
  addressLine2: "",
  country: "",
  countryName: "",
  state: "",
  stateName: "",
  city: "",
  zipCode: "",
};

const LOCATION_TYPE_OPTIONS = [
  { value: "WAREHOUSE", label: "Warehouse" },
  { value: "POINT", label: "Point (general facility)" },
  { value: "OCEAN_TERMINAL", label: "Ocean Terminal" },
  { value: "RAIL_TERMINAL", label: "Rail Terminal" },
  { value: "OTHER", label: "Other" },
];

const NEW_LOCATION_TEXT_FIELDS = {
  "Location Name": "locationName",
  "ERP Identifier": "customLocationIdentifier",
  "Address Line 1": "addressLine1",
  "Address Line 2": "addressLine2",
  City: "city",
  State: "state",
  Zip: "zipCode",
  Country: "country",
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\+?[0-9]{10,15}$/;

const ViewLocation = ({ location, isNew = false, parentRef, officeCode, onClose, onSuccess }) => {
  const [currentLocation, setCurrentLocation] = useState(isNew ? {...BLANK_NEW_LOCATION} : (location || null));
  const [isEditMode, setIsEditMode] = useState(isNew);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [newLocationErrors, setNewLocationErrors] = useState({});
  const { hasPrivilege } = usePrivilege();
  // LGX-1006: editing office/warehouse details and calendar hours used to be gated on "is a
  // shipper org" alone (any shipper user, not just admins) -- now also requires the dedicated
  // admin privilege. Keeping isShipper: it scopes this to shipper-side users at all, which is a
  // separate, pre-existing concern the ticket didn't ask to change.
  const isOfficeAdmin = hasPrivilege("officeManagement:ADMIN");

  const [isShipper, setIsShipper] = useState(false);
  const [formValues, setFormValues] = useState({
    appointmentUrl: "",
    contactName: "",
    phoneNumber: "",
    email: "",
    latitude: "",
    longitude: "",
    timezone: "",

    notes: "",
  });
  const [calendarSettings, setCalendarSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState(null);
  const [openEditWarehouseModal, setOpenEditWarehouseModal] = useState(false);
  const [mapLoading, setMapLoading] = useState(false);
  const timezoneOptions = useMemo(() => getAllTimezones(), []);

  // Check if user is a shipper from localStorage
  useEffect(() => {
    try {
      const storedData = localStorage.getItem('organizationTypes');
      if (storedData) {
        const organizationTypes = JSON.parse(storedData);

      const isShipper =
        (Array.isArray(organizationTypes) &&
          organizationTypes.some(type =>
            typeof type === "string" && type.toLowerCase().includes("shipper")
          )) ||
        (typeof organizationTypes === "string" &&
          organizationTypes.toLowerCase().includes("shipper"));
        setIsShipper(isShipper);
      }
    } catch (err) {
      console.error("Failed to parse organizationTypes from localStorage:", err);
      setIsShipper(false);
    }
  }, []);


  const formatDurationToHours = (durationInMinutes = 120) => {
    return (durationInMinutes / 60)
      .toFixed(2)
      .replace(/\.00$/, "");
  };

  // Update currentLocation when location prop changes
  useEffect(() => {
    if (isNew) return;
    setCurrentLocation(location || null);
  }, [location, isNew]);

  useEffect(() => {
    const fetchFreshLocationData = async () => {
      if (isNew) return;
      if (location?.id) {
        try {
          const { data } = await httpClient.get(
            `/api/locations/${location.id}`
          );
          setCurrentLocation(data);
        } catch (err) {
          console.error("Failed to fetch fresh location data:", err);
        }
      }
    };
    
    fetchFreshLocationData();
  }, [location?.id]); // Refetch when location ID changes

  // Update formValues whenever currentLocation changes
  useEffect(() => {
    if (currentLocation) {
      console.log("🔄 Updating formValues from currentLocation:", {
        contactName: currentLocation.contactName,
        phoneNumber: currentLocation.phoneNumber,
        email: currentLocation.email,
        notes: currentLocation.notes,
      });
      

      setFormValues({
        appointmentUrl: currentLocation.appointmentUrl ?? "",
        contactName: currentLocation.contactName ?? currentLocation.contactPerson ?? "",
        phoneNumber: currentLocation.phoneNumber ?? "",
        email: currentLocation.email ?? "",
        latitude: currentLocation.latitude ?? "",
        longitude: currentLocation.longitude ?? "",
        timezone: currentLocation.timezone ?? "",

        notes: currentLocation.notes ?? "",
      });
    }
  }, [currentLocation]);

  const transformCalendarResponseToSettings = useCallback(
    (apiResponse) => parseCalendarApiResponseToDockSettings(apiResponse),
    []
  );

  const fetchCalendarSettings = useCallback(async () => {
    const calendarRefId = location?.calendar?.calendarRefId;
    if (!calendarRefId) {
      setCalendarSettings(null);
      return;
    }

    setSettingsLoading(true);
    setSettingsError(null);
    try {
      const data = await fetchCalendarByRef(calendarRefId);
      setCalendarSettings(transformCalendarResponseToSettings(data || {}));
    } catch (err) {
      console.error("Failed to load calendar settings:", err);
      setSettingsError("Unable to load calendar settings.");
      setCalendarSettings(null);
    } finally {
      setSettingsLoading(false);
    }
  }, [location?.calendar?.calendarRefId, transformCalendarResponseToSettings]);

  useEffect(() => {
    fetchCalendarSettings();
  }, [fetchCalendarSettings]);

  const handleWarehouseSaved = useCallback(async () => {
    await fetchCalendarSettings();
    setOpenEditWarehouseModal(false);
  }, [fetchCalendarSettings]);

  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, "");
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return value;
  };

  const handleInputChange = (key, value) =>
    setFormValues((prev) => ({ ...prev, [key]: value }));

  const handlePhoneBlur = () => {
    setFormValues((prev) => ({
      ...prev,
      phoneNumber: formatPhoneNumber(prev.phoneNumber),
    }));
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setFormValues({
      appointmentUrl: currentLocation?.appointmentUrl ?? "",
      contactName:
        currentLocation?.contactName ?? currentLocation?.contactPerson ?? "",
      phoneNumber: currentLocation?.phoneNumber ?? "",
      email: currentLocation?.email ?? "",
      latitude: currentLocation?.latitude ?? "",
      longitude: currentLocation?.longitude ?? "",
      timezone: currentLocation?.timezone ?? "",

      notes: currentLocation?.notes ?? "",
    });
  };

  const handleSave = async () => {
    if (!currentLocation?.id) return;
    setIsSaving(true);
    setError(null);

    const notesValue = formValues.notes?.trim() || null;

    const payload = {
      id: currentLocation.id,
      locationName: currentLocation.locationName,
      addressLine1: currentLocation.addressLine1,
      addressLine2: currentLocation.addressLine2,
      city: currentLocation.city,
      state: currentLocation.state,
      zipCode: currentLocation.zipCode,
      country: currentLocation.country,
      latitude: formValues.latitude === "" ? null : Number(formValues.latitude),
      longitude: formValues.longitude === "" ? null : Number(formValues.longitude),
      timezone: formValues.timezone || null,

      appointmentUrl: formValues.appointmentUrl || null,
      contactName: formValues.contactName || null,
      phoneNumber: formValues.phoneNumber || null,
      email: formValues.email || null,
      notes: notesValue,
      fax: currentLocation.fax || null,
      customLocationIdentifier: currentLocation.customLocationIdentifier || null,
      locationRef: currentLocation.locationRef || null,
      locationType: currentLocation.locationType || null,
      locationCode: currentLocation.locationCode || null,
      officeCode: currentLocation.officeCode || null,
      hierarchyPath: currentLocation.hierarchyPath || null,
      isHeadquarters: currentLocation.isHeadquarters || null,

      parentLocation: currentLocation.parentLocation?.id
        ? { id: currentLocation.parentLocation.id }
        : null
    };

    try {
      const { data } = await httpClient.put(
        `/api/locations/Locrefupdate/${currentLocation.locationRef}`,
        payload,
        { headers: { accept: "*/*", "Content-Type": "application/json" } }
      );
      
      
      const updated = data && typeof data === "object" ? data : payload;
      setCurrentLocation(updated);
      setIsEditMode(false);
      
    } catch (err) {
      setError("Failed to update location. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  // Prepare key-value pairs
  const mapLink =
    currentLocation?.latitude != null &&
    currentLocation?.longitude != null
      ? `https://www.google.com/maps/?q=${currentLocation.latitude},${currentLocation.longitude}`
      : null;

  const entries = {
    "Location Name": currentLocation?.locationName,
    ...(isNew ? {"Location Type": currentLocation?.locationType} : {}),
    "ERP Identifier": currentLocation?.customLocationIdentifier,
    "Address Line 1": currentLocation?.addressLine1,
    "Address Line 2": currentLocation?.addressLine2,
    Country: currentLocation?.country,
    State: currentLocation?.state,
    City: currentLocation?.city,
    Zip: currentLocation?.zipCode,

    "Appointment URL": !isEditMode && formValues.appointmentUrl 
      ? linkifyText(formValues.appointmentUrl)
      : formValues.appointmentUrl,
    "Contact Person": formValues.contactName,
    "Phone Number": formValues.phoneNumber,
    Email: !isEditMode && formValues.email
      ? linkifyEmails(formValues.email, "text-blue-600 dark:text-blue-400 hover:underline")
      : formValues.email,
    Latitude: formValues.latitude,
    Longitude: formValues.longitude,
    Timezone:
      !isEditMode && formValues.timezone
        ? `${formValues.timezone} (${getUtcOffsetLabel(formValues.timezone)})`
        : formValues.timezone,

  };

  if (!isEditMode) {
    entries["Map URL"] = mapLink ? (
      <a
        href={mapLink}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:underline"
      >
        View Map
      </a>
    ) : (
      "—"
    );
  }

  useEffect(() => {
    setMapLoading(!!mapLink);
  }, [mapLink]);

  const editableFieldConfig = {
    "Appointment URL": "appointmentUrl",
    "Contact Person": "contactName",
    "Phone Number": "phoneNumber",
    Email: "email",
    Latitude: "latitude",
    Longitude: "longitude",
    Timezone: "timezone",
  };

  if (!location && !isNew) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 text-lg">
        No location data available.
      </div>
    );
  }

  // --- New-warehouse field handling (create mode) ---
  const handleNewLocationChange = (field, value) => {
    setCurrentLocation((prev) => ({...(prev || {}), [field]: value}));
    if (newLocationErrors[field]) {
      setNewLocationErrors((prev) => {
        const next = {...prev};
        delete next[field];
        return next;
      });
    }
  };

  const handleSaveNewLocation = async () => {
    // State and Zip Code are deliberately NOT required -- not every country uses them (UK has no
    // US-style "state"; Singapore is a city-state; Guatemala and others don't consistently use
    // postal codes).
    const required = ["locationName", "addressLine1", "city", "country"];
    const errors = {};
    required.forEach((field) => {
      if (!String(currentLocation?.[field] ?? "").trim()) errors[field] = "Required";
    });
    if (formValues.email && !EMAIL_REGEX.test(formValues.email)) {
      errors.email = "Invalid email format";
    }
    // Users naturally type phone numbers with spaces/parens/dashes (e.g. "+1 209 400 1234"),
    // but PHONE_REGEX (matching the backend's own @Pattern) only accepts a bare digit string --
    // strip that formatting before validating/submitting rather than rejecting valid numbers.
    const cleanedPhone = String(formValues.phoneNumber ?? "").replace(/[\s().-]/g, "");
    if (cleanedPhone && !PHONE_REGEX.test(cleanedPhone)) {
      errors.phoneNumber = "Invalid phone number (10-15 digits, optional +).";
    }
    if (Object.keys(errors).length > 0) {
      setNewLocationErrors(errors);
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      const token = Cookies.get("authToken");
      const payload = {
        locationName: currentLocation.locationName,
        locationType: currentLocation.locationType || "WAREHOUSE",
        customLocationIdentifier: currentLocation.customLocationIdentifier || null,
        addressLine1: currentLocation.addressLine1,
        addressLine2: currentLocation.addressLine2,
        city: currentLocation.city,
        state: currentLocation.state,
        zipCode: currentLocation.zipCode,
        country: currentLocation.country,
        appointmentUrl: formValues.appointmentUrl || null,
        contactName: formValues.contactName || null,
        phoneNumber: cleanedPhone || null,
        email: formValues.email || null,
      };
      await addChildLocation(parentRef, token, officeCode, payload);
      toast.success(`${currentLocation.locationName} added.`);
      onSuccess?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.content || "Failed to add warehouse.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="w-full h-full flex flex-col transition-opacity duration-500 bg-gray-50 dark:bg-acs-dark-bg">
      
      {/* === Top Header (Mirroring PreferredPartnerDetails) === */}
      <div className="sticky top-0 z-10 bg-white dark:bg-acs-dark-surface shadow-md p-fluid-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-fluid-3">
        <div className="flex items-center gap-fluid-3">
          <div className="w-20 h-20 rounded-full bg-acs-accent/20 dark:bg-acs-accent/30 flex items-center justify-center shadow-inner">
            <Building2 className="w-10 h-10 text-blue-600 dark:text-acs-accent" />
          </div>

          <div>
            <h2 className="text-fluid-xl font-semibold text-gray-900 dark:text-acs-dark-text">
              {isNew ? "Add Warehouse" : (location.locationName || "Untitled Location")}
            </h2>
            <p className="text-sm text-gray-500 dark:text-acs-dark-subtext">
              {isNew ? "Enter details to create a new warehouse" : `${location.city}, ${location.state}`}
            </p>

            {!isNew && (
            <div className="mt-fluid-1 flex items-center gap-2">
              <span className="bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 px-2 py-[2px] text-xs rounded-md font-medium">
                Active Location
              </span>
            </div>
            )}
          </div>
        </div>
      </div>

      {/* === Main Content === */}
      <div className="flex-1 overflow-y-auto bg-white dark:bg-acs-dark-bg w-full p-fluid-3 space-y-fluid-3">

        {/* === Location Info === */}
        <div className={sectionCardClass}>
          <div className={`${sectionHeaderBarClass} flex items-center justify-between`}>
            <span>{isNew ? "Warehouse Information" : "Location Information"}</span>
            <div className="flex items-center gap-2">
              {error && (
                <span className="text-xs text-red-200 whitespace-nowrap">
                  {error}
                </span>
              )}
              {(isNew || (isShipper && isOfficeAdmin)) && (
                <>
                  {isEditMode ? (
                    <>
                      <button
                        onClick={isNew ? handleSaveNewLocation : handleSave}
                        disabled={isSaving}
                        className="px-3 py-1 text-xs rounded-md bg-white/10 border border-white/30 text-white hover:bg-white/20 disabled:opacity-60"
                      >
                        {isSaving ? (isNew ? "Adding..." : "Saving...") : (isNew ? "Add Warehouse" : "Save")}
                      </button>
                      <button
                        onClick={isNew ? () => onClose?.() : handleCancelEdit}
                        disabled={isSaving}
                        className="px-3 py-1 text-xs rounded-md border border-white/30 text-white hover:bg-white/10"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setIsEditMode(true)}
                      className="p-1.5 rounded-full hover:bg-white/10 text-white"
                      title="Edit Location"
                    >
                      <Pencil size={16} />
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          <div className={sectionFieldGridClass}>
            {Object.entries(entries).map(([label, value]) => {
              if (isEditMode && label === "Map URL") return null;
              // Map URL is an action link, not a data value -- rendering it through the shared
              // DisplayDetail box (bordered, input-like background) made a plain clickable link
              // look like it was sitting inside a disabled text field. Same label styling as
              // every other field, just without the box around the value.
              if (label === "Map URL") {
                return (
                  <div key={label} className="flex flex-col min-w-[clamp(8rem,12vw,10rem)]">
                    <span className="text-dense-xs text-gray-500 dark:text-acs-dark-subtext">{label}</span>
                    <div className="mt-fluid-0.25 py-fluid-1.5">{value}</div>
                  </div>
                );
              }
              const fieldKey = editableFieldConfig[label];
              const newLocationFieldKey = isNew ? NEW_LOCATION_TEXT_FIELDS[label] : null;
              const isRequiredNewField = isNew && newLocationFieldKey
                && !["addressLine2", "state", "zipCode", "customLocationIdentifier"].includes(newLocationFieldKey);
              const isEditable = isEditMode && (fieldKey || newLocationFieldKey || (isNew && label === "Location Type"));
              const errorText = newLocationFieldKey ? newLocationErrors[newLocationFieldKey] : null;
              if (isEditable && label === "Location Type") {
                return (
                  <FormField key={label} label={label} required={isRequiredNewField} error={errorText}>
                    <select
                      value={currentLocation?.locationType ?? "WAREHOUSE"}
                      onChange={(e) => handleNewLocationChange("locationType", e.target.value)}
                      className="w-full bg-transparent outline-none appearance-none cursor-pointer pr-fluid-4 dark:text-acs-dark-text [&>option]:text-gray-900"
                    >
                      {LOCATION_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </FormField>
                );
              }

              if (isEditable && label === "Timezone") {
                return (
                  <GeoTypeaheadField
                    key={label}
                    label={label}
                    required={isRequiredNewField}
                    error={errorText}
                    nameValue={formValues.timezone ? `${formValues.timezone} (${getUtcOffsetLabel(formValues.timezone)})` : ""}
                    minQueryLength={1}
                    searchFn={(q) => Promise.resolve({
                      data: timezoneOptions
                        .filter((tz) => tz.toLowerCase().includes(q.toLowerCase()))
                        .map((tz) => ({ code: tz, name: `${tz} (${getUtcOffsetLabel(tz)})` })),
                    })}
                    onSelect={(opt) => handleInputChange("timezone", opt.code)}
                  />
                );
              }

              if (isEditable && isNew && (label === "Country" || label === "State" || label === "City")) {
                if (label === "Country") {
                  return (
                    <GeoTypeaheadField
                      key={label}
                      label={label}
                      required
                      error={errorText}
                      nameValue={currentLocation?.countryName}
                      searchFn={(q) => searchCountries(q)}
                      codeKeys={["code", "countryCode", "isoCode"]}
                      nameKeys={["name", "countryName"]}
                      onSelect={(opt) => {
                        handleNewLocationChange("country", opt.code || opt.name);
                        handleNewLocationChange("countryName", opt.name || opt.code);
                        handleNewLocationChange("state", "");
                        handleNewLocationChange("stateName", "");
                        handleNewLocationChange("city", "");
                      }}
                    />
                  );
                }
                if (label === "State") {
                  return (
                    <GeoTypeaheadField
                      key={label}
                      label={label}
                      disabled={!currentLocation?.country}
                      error={errorText}
                      nameValue={currentLocation?.stateName}
                      searchFn={(q) => searchStatesByCountry(currentLocation?.country, q)}
                      codeKeys={["code", "stateCode", "isoCode"]}
                      nameKeys={["name", "stateName"]}
                      placeholder={currentLocation?.country ? undefined : "Select a country first"}
                      onSelect={(opt) => {
                        handleNewLocationChange("state", opt.code || opt.name);
                        handleNewLocationChange("stateName", opt.name || opt.code);
                        handleNewLocationChange("city", "");
                      }}
                    />
                  );
                }
                return (
                  <GeoTypeaheadField
                    key={label}
                    label={label}
                    required
                    disabled={!currentLocation?.country || !currentLocation?.state}
                    error={errorText}
                    nameValue={currentLocation?.city}
                    searchFn={(q) => searchCitiesByCountryState(currentLocation?.country, currentLocation?.state, q)}
                    codeKeys={["code", "cityCode"]}
                    nameKeys={["name", "cityName"]}
                    placeholder={currentLocation?.state ? undefined : "Select a state first"}
                    onPersistNew={async (typed) => {
                      const resp = await saveNewCity(currentLocation?.country, currentLocation?.state, typed);
                      return resp?.data
                        ? {code: resp.data.cityName || typed, name: resp.data.cityName || typed}
                        : null;
                    }}
                    onSelect={(opt) => handleNewLocationChange("city", opt.name || opt.code)}
                  />
                );
              }

              return (
                <div key={label}>
                  <DisplayDetail
                    label={label}
                    required={isRequiredNewField}
                    value={newLocationFieldKey ? currentLocation?.[newLocationFieldKey] : (isEditable ? formValues[fieldKey] : value)}
                    isEditable={isEditable}
                    inputType={label === "Latitude" || label === "Longitude" ? "number" : label === "Phone Number" ? "tel" : "text"}
                    onChange={(v) =>
                      newLocationFieldKey
                        ? handleNewLocationChange(newLocationFieldKey, v)
                        : handleInputChange(fieldKey, v)
                    }
                    onBlur={label === "Phone Number" ? handlePhoneBlur : undefined}
                  />
                  <ErrorHint message={errorText} />
                </div>
              );
            })}

            {/* Notes Section - Full Width */}
            <div className="flex flex-col pt-2 border-t border-gray-200 dark:border-acs-dark-border">
              <span className="font-medium text-gray-600 dark:text-acs-dark-subtext mb-1">
                Notes:
              </span>
              {isEditMode ? (
                <textarea
                  value={formValues.notes ?? ""}
                  onChange={(e) => handleInputChange("notes", e.target.value)}
                  className="w-full px-2 py-2 border border-gray-300 rounded-md text-sm bg-white min-h-[100px] resize-y"
                  placeholder="Add any notes about this location..."
                />
              ) : (
                <div className="text-gray-800 dark:text-acs-dark-text whitespace-pre-wrap">
                  {formValues.notes && formValues.notes !== "—" 
                    ? linkifyText(formValues.notes)
                    : "—"
                  }
                </div>
              )}
            </div>
          </div>

          {/* Notes — full width, below the grid */}
          <div className={`${sectionContentClass} pt-0`}>
            <DisplayDetail
              label="Notes"
              value={isEditMode ? formValues.notes : (formValues.notes && formValues.notes !== "—" ? linkifyText(formValues.notes) : formValues.notes)}
              isEditable={isEditMode}
              multiline
              onChange={(v) => handleInputChange("notes", v)}
              className="col-span-6"
            />
          </div>
        </div>

        {/* === Map View === */}
        {!isNew && (
        <div className={`${sectionCardClass} overflow-hidden`}>
          {mapLink ? (
            <div className="relative min-h-[300px]">
              {mapLoading && (
                <div className="absolute inset-0 bg-white/60 dark:bg-black/40 flex items-center justify-center z-10">
                  <Loader message="Loading map..." />
                </div>
              )}
              <iframe
                title="Location Map"
                src={`https://maps.google.com/maps?q=${currentLocation.latitude},${currentLocation.longitude}&z=14&output=embed`}
                className="w-full h-full min-h-[300px]"
                loading="lazy"
                allowFullScreen
                onLoad={() => setMapLoading(false)}
              />
            </div>
          ) : (
            <div className="min-h-[200px] flex items-center justify-center text-sm text-gray-500 dark:text-acs-dark-subtext bg-gray-100 dark:bg-[#2A2D35]">
              Add latitude and longitude to preview this location on the map.
            </div>
          )}
        </div>
        )}

        {!isNew && location?.calendar?.calendarRefId && (
          <div className="md:col-span-2 bg-gray-50 dark:bg-acs-dark-surface border border-gray-100 dark:border-acs-dark-border rounded-xl shadow-sm p-fluid-3">
            <div className="flex items-center justify-between mb-fluid-2">
              <h3 className="text-fluid-lg font-semibold text-gray-900 dark:text-acs-dark-text">
                Calendar Configuration
              </h3>
              <div className="flex items-center gap-2">
                {settingsError && (
                  <span className="text-xs text-red-500 whitespace-nowrap">
                    {settingsError}
                  </span>
                )}
                {isShipper && isOfficeAdmin && (
                  <button
                    type="button"
                    onClick={() => setOpenEditWarehouseModal(true)}
                    className="p-2 rounded-full hover:bg-gray-100 text-gray-500"
                    title="Edit warehouse calendar"
                  >
                    <Pencil size={16} />
                  </button>
                )}
              </div>
            </div>

            {settingsLoading ? (
              <p className="text-gray-500 dark:text-acs-dark-subtext">
                Loading calendar settings...
              </p>
            ) : calendarSettings ? (
              <DockDoorSettings
                settings={calendarSettings}
                onChange={() => {}}
                isViewOnly={true}
                isWarehouseView={true}
              />
            ) : (
              <p className="text-gray-500 dark:text-acs-dark-subtext">
                No calendar settings available.
              </p>
            )}
          </div>
        )}
      </div>

      {openEditWarehouseModal && (
        <EditWarehouseModal
          warehouseName={currentLocation?.locationName}
          settings={calendarSettings}
          onClose={() => setOpenEditWarehouseModal(false)}
          onSaved={handleWarehouseSaved}
        />
      )}
    </div>
  );
};

export default ViewLocation;